"use client";
import { useTranslations } from "next-intl";
import { useState, useRef, useEffect } from "react";
import { useRouter } from "@/navigation";

type Props = {
	className?: string;
	lastScrollPosition?: number;
};

export default function Search({ className, lastScrollPosition }: Props) {
	const t = useTranslations();
	const router = useRouter();
	const [inputValue, setInputValue] = useState("");
	const [isExpanded, setIsExpanded] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	// 处理点击外部收起搜索框
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node) &&
				isExpanded &&
				!inputValue
			) {
				setIsExpanded(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isExpanded, inputValue]);

	const handleSearch = async () => {
		if (!inputValue.trim()) return;

		try {
			setIsLoading(true);
			await router.push(`/search?query=${encodeURIComponent(inputValue.trim())}`);
		} finally {
			setIsLoading(false);
		}
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(event.target.value);
	};

	const handleSearchClick = () => {
		if (isExpanded && inputValue.trim()) {
			// 如果已展开且有输入内容，则执行搜索
			handleSearch();
		} else {
			// 否则切换展开状态
			setIsExpanded(!isExpanded);
			if (!isExpanded) {
				setTimeout(() => {
					inputRef.current?.focus();
				}, 100);
			}
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			handleSearch();
		} else if (event.key === "Escape") {
			setIsExpanded(false);
			inputRef.current?.blur();
		}
	};

	const clearInput = () => {
		setInputValue("");
		inputRef.current?.focus();
	};

	return (
		<div ref={containerRef} className={`b-flex search relative h-[36px] ${className}`}>
			<div
				className={`flex items-center  overflow-hidden transition-all duration-300 ease-in-out ${
					isExpanded ? "w-[250px] rounded-full bg-[#F8F8F8]" : "w-[36px] rounded-full bg-transparent"
				}`}
			>
				<input
					ref={inputRef}
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					type="text"
					className={`h-[36px] w-full bg-transparent px-4 py-2 outline-none transition-all duration-300 ease-in-out ${
						isExpanded ? "opacity-100" : "opacity-0"
					}`}
					style={{ color: "#000" }}
				/>
				<div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-1">
					{isExpanded && inputValue && (
						<button
							onClick={clearInput}
							className="flex h-[32px] w-[32px] items-center justify-center rounded-full text-gray-400 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-600"
							title={t("nav.clear")}
						>
							<i className="ri-close-line text-lg" />
						</button>
					)}
					<button
						onClick={handleSearchClick}
						disabled={isLoading}
						className={`flex h-[36px] w-[36px] items-center justify-center rounded-full transition-colors duration-200
							${!isExpanded ? "bg-transparent" : ""}
							${lastScrollPosition ? "hover:bg-gray-100" : ""}
							${isLoading ? "cursor-wait opacity-50" : ""}`}
					>
						{isLoading ? (
							<i className="ri-loader-4-line animate-spin text-lg text-black" />
						) : (
							<i
								className={`ri-search-line text-2xl transition-all duration-200
									${lastScrollPosition ? "text-black" : "text-white"}
									${isExpanded && inputValue ? "!text-blue-600" : ""}
									${isExpanded ? "!text-black opacity-50 hover:opacity-100" : ""}`}
							/>
						)}
					</button>
				</div>
			</div>
		</div>
	);
}
